import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  TextField,
  Snackbar,
  Slider,
  InputLabel,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { SketchPicker } from 'react-color';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AdminLogin from '../components/AdminLogin';

const Input = styled('input')({
  display: 'none',
});

const PreviewContainer = styled(Box)({
  position: 'relative',
  cursor: 'crosshair',
  '& img': {
    maxWidth: '100%',
    height: 'auto',
  },
});

const PositionMarker = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '10px',
  height: '10px',
  backgroundColor: 'red',
  borderRadius: '50%',
  transform: 'translate(-50%, -50%)',
  pointerEvents: 'none',
}));

const PreviewText = styled(Typography)(({ x, y, color, weight, size }) => ({
  position: 'absolute',
  left: `${x}%`,
  top: `${y}%`,
  transform: 'translate(-50%, -50%)',
  color: color,
  fontWeight: weight,
  fontSize: `clamp(${size * 0.4}px, ${size * 0.12}vw, ${size}px)`, // Responsive scaling: min, preferred, max
  pointerEvents: 'none',
  textShadow: '1px 1px 2px rgba(255,255,255,0.8), -1px -1px 2px rgba(255,255,255,0.8)',
  whiteSpace: 'nowrap',
}));

const AuthenticatedImage = ({ url, alt, height, sx }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchImage = async () => {
      try {
        const token = localStorage.getItem('adminToken');
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) throw new Error('Failed to load image');

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        setImageSrc(objectUrl);
      } catch (error) {
        console.error('Error loading image:', error);
        setError(true);
      }
    };

    fetchImage();

    // Cleanup function to revoke object URL
    return () => {
      if (imageSrc) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [url]);

  if (error) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.200',
          ...sx
        }}
      >
        <Typography color="text.secondary">Failed to load image</Typography>
      </Box>
    );
  }

  if (!imageSrc) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...sx
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <CardMedia
      component="img"
      height={height}
      image={imageSrc}
      alt={alt}
      sx={sx}
    />
  );
};

const AdminPanel = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [namePosition, setNamePosition] = useState({ x: 50, y: 50 });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [shareableLink, setShareableLink] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const previewRef = useRef(null);

  // New state for text styling
  const defaultTextStyle = {
    fontSize: 5,
    fontWeight: 400,
    color: '#000000',
  };

  // Initialize textStyle with default values
  const [textStyle, setTextStyle] = useState(defaultTextStyle);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showPreviewText, setShowPreviewText] = useState(true);
  const previewText = "Firstname Lastname";
  const [cards, setCards] = useState([]);
  const [editingCard, setEditingCard] = useState(null);
  const [deleteConfirmCard, setDeleteConfirmCard] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [copiedCardId, setCopiedCardId] = useState(null);

  // Add loading states
  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingCards, setIsLoadingCards] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Add authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  // Add this effect to check authentication on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      setIsCheckingAuth(false);
      return;
    }

    try {
      const response = await fetch('/api/admin/verify', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem('adminToken');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setIsCheckingAuth(false);
    }
  };

  // Add authentication headers to all fetch calls
  const authenticatedFetch = async (url, options = {}) => {
    const token = localStorage.getItem('adminToken');
    return fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
      },
    });
  };

  // Fetch all cards on component mount
  useEffect(() => {
    fetchCards();
  }, []);

  const fetchCards = async () => {
    setIsLoadingCards(true);
    try {
      const response = await authenticatedFetch('/api/cards');
      const data = await response.json();
      setCards(data);
    } catch (error) {
      console.error('Error fetching cards:', error);
    } finally {
      setIsLoadingCards(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handlePreviewClick = (event) => {
    if (!previewRef.current) return;

    const rect = previewRef.current.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    setNamePosition({ x, y });
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setIsUploading(true);

    const formData = new FormData();
    formData.append('cardFile', selectedFile);
    formData.append('namePosition', JSON.stringify(namePosition));
    formData.append('textStyle', JSON.stringify(textStyle));

    try {
      const response = await authenticatedFetch('/api/cards', {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      const link = `${window.location.origin}/card/${data.id}`;
      setShareableLink(link);
      setSnackbarOpen(true);
      fetchCards();
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleColorChange = (color) => {
    setTextStyle(prev => ({
      ...prev,
      color: color.hex
    }));
  };

  const handleEdit = (card) => {
    setEditingCard(card);
    setNamePosition(card.namePosition);
    // Use card's textStyle if it exists, otherwise use default
    setTextStyle(card.textStyle || defaultTextStyle);
    setEditDialogOpen(true);
  };

  const handleDelete = async (cardId) => {
    setIsDeleting(true);
    try {
      const response = await authenticatedFetch(`/api/cards/${cardId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setCards(cards.filter(card => card.id !== cardId));
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error deleting card:', error);
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleUpdateCard = async () => {
    if (!editingCard) return;
    setIsUpdating(true);

    try {
      const response = await authenticatedFetch(`/api/cards/${editingCard.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          namePosition,
          textStyle,
        }),
      });

      if (response.ok) {
        const updatedCard = await response.json();
        setCards(cards.map(card =>
          card.id === updatedCard.id ? updatedCard : card
        ));
        setEditDialogOpen(false);
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error updating card:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCopyLink = (cardId) => {
    const url = `${window.location.origin}/card/${cardId}`;

    // Try using the Clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(url).then(() => {
        setCopiedCardId(cardId);
        setTimeout(() => setCopiedCardId(null), 2000);
      }).catch(err => {
        console.error('Failed to copy:', err);
        fallbackCopyToClipboard(url, cardId);
      });
    } else {
      // Fallback for browsers that don't support the Clipboard API
      fallbackCopyToClipboard(url, cardId);
    }
  };

  // Add the fallback copy function
  const fallbackCopyToClipboard = (text, cardId) => {
    try {
      // Create temporary textarea
      const textArea = document.createElement('textarea');
      textArea.value = text;

      // Make it invisible
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);

      // Select and copy
      textArea.focus();
      textArea.select();
      document.execCommand('copy');

      // Cleanup
      textArea.remove();

      // Show success state
      setCopiedCardId(cardId);
      setTimeout(() => setCopiedCardId(null), 2000);
    } catch (err) {
      console.error('Fallback copy failed:', err);
    }
  };

  if (isCheckingAuth) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!isAuthenticated) {
    return <AdminLogin onLogin={() => setIsAuthenticated(true)} />;
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'primary.main' }}>
          Admin Panel
        </Typography>

        <Paper
          sx={{
            p: 3,
            backgroundColor: 'background.paper',
            boxShadow: 1
          }}
        >
          <Typography variant="h5" gutterBottom>
            Upload New Card
          </Typography>
          <Box sx={{ mb: 3 }}>
            <label htmlFor="card-file">
              <Input
                accept="image/*,.svg"
                id="card-file"
                type="file"
                onChange={handleFileSelect}
              />
              <Button variant="contained" component="span">
                Select Card File
              </Button>
            </label>
          </Box>

          {previewUrl && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="subtitle1">
                  Click on the image to set name position
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setShowPreviewText(!showPreviewText)}
                >
                  {showPreviewText ? 'Hide' : 'Show'} Preview Text
                </Button>
              </Box>
              <PreviewContainer
                ref={previewRef}
                onClick={handlePreviewClick}
              >
                <img
                  src={previewUrl}
                  alt="Preview"
                />
                <PositionMarker
                  sx={{
                    left: `${namePosition.x}%`,
                    top: `${namePosition.y}%`,
                  }}
                />
                {showPreviewText && (
                  <PreviewText
                    x={namePosition.x}
                    y={namePosition.y}
                    color={textStyle.color}
                    weight={textStyle.fontWeight}
                    size={textStyle.fontSize}
                  >
                    {previewText}
                  </PreviewText>
                )}
              </PreviewContainer>
            </Box>
          )}

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Text Styling
            </Typography>

            <Box sx={{ mb: 2 }}>
              <InputLabel>Font Size (% of image height)</InputLabel>
              <Slider
                value={textStyle.fontSize}
                onChange={(_, value) => setTextStyle(prev => ({ ...prev, fontSize: value }))}
                min={1}
                max={20}
                step={0.5}
                valueLabelDisplay="auto"
                valueLabelFormat={value => `${value}%`}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <InputLabel>Font Weight</InputLabel>
              <Slider
                value={textStyle.fontWeight}
                onChange={(_, value) => setTextStyle(prev => ({ ...prev, fontWeight: value }))}
                min={100}
                max={900}
                step={100}
                valueLabelDisplay="auto"
                marks={[
                  { value: 100, label: '100' },
                  { value: 400, label: 'Normal' },
                  { value: 700, label: 'Bold' },
                  { value: 900, label: '900' },
                ]}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setShowColorPicker(!showColorPicker)}
                sx={{ mb: 1 }}
              >
                {showColorPicker ? 'Close Color Picker' : 'Open Color Picker'}
              </Button>
              {showColorPicker && (
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <SketchPicker
                    color={textStyle.color}
                    onChange={handleColorChange}
                  />
                </Box>
              )}
            </Box>
          </Box>

          <Button
            variant="contained"
            color="primary"
            onClick={handleUpload}
            disabled={!selectedFile || isUploading}
            sx={{ mt: 2 }}
          >
            {isUploading ? 'Uploading...' : 'Upload Card'}
          </Button>

          {shareableLink && (
            <Box sx={{ mt: 3 }}>
              <TextField
                fullWidth
                label="Shareable Link"
                value={shareableLink}
                InputProps={{ readOnly: true }}
              />
            </Box>
          )}
        </Paper>

        {/* Cards List */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Existing Cards
          </Typography>
          {isLoadingCards ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              {cards.map((card) => (
                <Grid item xs={12} sm={6} md={4} key={card.id}>
                  <Card>
                    <Box sx={{ position: 'relative' }}>
                      <AuthenticatedImage
                        url={`/api/cards/${card.id}/base-image`}
                        height={140}
                        alt={`Card ${card.id}`}
                        sx={{ objectFit: 'contain', bgcolor: 'background.default' }}
                      />
                      {/* Add loading overlay for individual card images */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'rgba(255, 255, 255, 0.7)',
                          opacity: 0,
                          transition: 'opacity 0.3s',
                          '&.loading': {
                            opacity: 1,
                          },
                        }}
                        className={isDeleting && deleteConfirmCard?.id === card.id ? 'loading' : ''}
                      >
                        <CircularProgress />
                      </Box>
                    </Box>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        Created: {new Date(card.createdAt).toLocaleDateString()}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <IconButton
                        onClick={() => handleEdit(card)}
                        aria-label="edit"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          setDeleteConfirmCard(card);
                          setDeleteDialogOpen(true);
                        }}
                        aria-label="delete"
                      >
                        <DeleteIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleCopyLink(card.id)}
                        aria-label="copy link"
                        color={copiedCardId === card.id ? "success" : "default"}
                      >
                        <ContentCopyIcon />
                      </IconButton>
                      {copiedCardId === card.id && (
                        <Typography
                          variant="caption"
                          color="success.main"
                          sx={{ ml: 1 }}
                        >
                          Copied!
                        </Typography>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      </Box>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Card Settings</DialogTitle>
        <DialogContent>
          {editingCard && (
            <Box sx={{ mt: 2 }}>
              <PreviewContainer
                ref={previewRef}
                onClick={handlePreviewClick}
              >
                <AuthenticatedImage
                  url={`/api/cards/${editingCard.id}/base-image`}
                  height="auto"
                  alt="Preview"
                  sx={{ width: '100%', height: 'auto' }}
                />
                <PositionMarker
                  sx={{
                    left: `${namePosition.x}%`,
                    top: `${namePosition.y}%`,
                  }}
                />
                {showPreviewText && (
                  <PreviewText
                    x={namePosition.x}
                    y={namePosition.y}
                    color={textStyle.color}
                    weight={textStyle.fontWeight}
                    size={textStyle.fontSize}
                  >
                    {previewText}
                  </PreviewText>
                )}
              </PreviewContainer>

              {/* Text styling controls */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Text Styling
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <InputLabel>Font Size (% of image height)</InputLabel>
                  <Slider
                    value={textStyle.fontSize}
                    onChange={(_, value) => setTextStyle(prev => ({ ...prev, fontSize: value }))}
                    min={1}
                    max={20}
                    step={0.5}
                    valueLabelDisplay="auto"
                    valueLabelFormat={value => `${value}%`}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <InputLabel>Font Weight</InputLabel>
                  <Slider
                    value={textStyle.fontWeight}
                    onChange={(_, value) => setTextStyle(prev => ({ ...prev, fontWeight: value }))}
                    min={100}
                    max={900}
                    step={100}
                    valueLabelDisplay="auto"
                    marks={[
                      { value: 100, label: '100' },
                      { value: 400, label: 'Normal' },
                      { value: 700, label: 'Bold' },
                      { value: 900, label: '900' },
                    ]}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setShowColorPicker(!showColorPicker)}
                    sx={{ mb: 1 }}
                  >
                    {showColorPicker ? 'Close Color Picker' : 'Open Color Picker'}
                  </Button>
                  {showColorPicker && (
                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                      <SketchPicker
                        color={textStyle.color}
                        onChange={handleColorChange}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={isUpdating}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateCard}
            variant="contained"
            disabled={isUpdating}
          >
            {isUpdating ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                Saving...
              </Box>
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this celebration card?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={() => handleDelete(deleteConfirmCard?.id)}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                Deleting...
              </Box>
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add a global loading backdrop for major operations */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isUploading || isUpdating}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message="Link generated successfully!"
      />
    </Container>
  );
};

export default AdminPanel; 